import { formatDisplayDate } from './date-util';

const _startWithYear = new RegExp('^([0-9]{4}.+)');

// if it were me I would have the type guard be Date but this check is used to determine if a string can be used as a
// date, so I had it return a string
const _isDateValue = (value: unknown): value is string => {
  // a very rough heuristic to determine if the 'value' represent a date
  return typeof value === 'string' && _startWithYear.test(value) && isNaN(Number(value)) && !isNaN(Date.parse(value));
};

const _isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object';

const _isString = (value: unknown): value is string => typeof value === 'string';

const _isJsonString = (value: unknown) => {
  try {
    if (typeof value !== 'string') {
      return false;
    }

    JSON.parse(value);
  } catch (_e) {
    return false;
  }
  return true;
};

const _isStringEncodedArray = (value: unknown): value is string =>
  _isString(value) && _isJsonString(value) && Array.isArray(JSON.parse(value));

const _isCheckboxValue = (value: unknown) => {
  return true === value || false === value || 'true' === value || 'false' === value;
};

const _formatCheckboxValue = (value: unknown) => {
  if (true === value || 'true' === value) {
    return 'True';
  }
  if (false === value || 'false' === value) {
    return 'False';
  }
  return value?.toString();
};

const _extractObjectDisplayValue = (item: Record<string, unknown>) => {
  if (Object.hasOwn(item, 'displayName')) {
    return item.displayName;
  } else if (Object.hasOwn(item, 'text')) {
    return item.text;
  } else if (Object.hasOwn(item, 'value')) {
    return item.value;
  } else if (
    Object.hasOwn(item, 'caption') &&
    _isObject(item.caption) &&
    Object.hasOwn(item.caption, 'translations') &&
    Array.isArray(item.caption.translations)
  ) {
    return item.caption?.translations[0]?.value;
  } else if (Object.hasOwn(item, 'firstName') && Object.hasOwn(item, 'lastName')) {
    return item.lastName + ' ' + item.firstName;
  } else if (Object.keys(item).length > 1) {
    // special case added for handling the history field
    return Object.values(item)
      .map((val) => {
        // ignore nested objects
        if (_isObject(val)) {
          return '';
        } else if (_isDateValue(val)) {
          // handle date values
          return formatDisplayDate(new Date(val));
        }
        return val?.toString() || '';
      })
      .join(' ')
      .trim();
  }
  return '';
};

// Used in various grids to extract and format the display value
export const resolveFieldDisplayValue = (fieldValue: unknown) => {
  if (Array.isArray(fieldValue) || _isStringEncodedArray(fieldValue)) {
    const arr = Array.isArray(fieldValue) ? fieldValue : JSON.parse(fieldValue);
    return arr
      .map((_item: unknown) => {
        if (_isObject(_item)) {
          return _extractObjectDisplayValue(_item);
        }
        return _item?.toString() || '';
      })
      .join(', ');
  } else if (_isObject(fieldValue)) {
    return _extractObjectDisplayValue(fieldValue);
  } else if (_isDateValue(fieldValue)) {
    return formatDisplayDate(new Date(fieldValue));
  } else if (_isCheckboxValue(fieldValue?.toString())) {
    return _formatCheckboxValue(fieldValue);
  }
  return fieldValue?.toString() || '';
};
